import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import PasswordRequirements from '../components/PasswordRequirements';
import './AccountInfo.css';

function AccountInfo() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = (password) => {
    if (password.length < 8) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/\d/.test(password)) return false;
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(password)) return false;
    return true;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    if (!validatePassword(passwordData.newPassword)) {
      setMessage({ type: 'error', text: 'Password does not meet security requirements' });
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/change-password', {
        newPassword: passwordData.newPassword,
      });

      const data = response.data;

      if (response.status === 200) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({ newPassword: '', confirmPassword: '' });
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (err) {
      // Show the actual error message from backend
      const errorMessage = err.response?.data?.message || 'Failed to change password. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="account" />

      <div className="content">
        <h1 className="page-title">Account Information</h1>

        {message.text && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
            {message.text}
          </div>
        )}

        <div className="account-info-container">
          {/* Account Details Section */}
          <div className="account-section">
            <h2 className="section-title">Account Details</h2>
            <div className="info-grid">
              <div className="info-row">
                <label>Account ID:</label>
                <span>{user?.id || 'N/A'}</span>
              </div>
              <div className="info-row">
                <label>Username:</label>
                <span>{user?.userId || 'N/A'}</span>
              </div>
              <div className="info-row">
                <label>Full Name:</label>
                <span>{user?.name || 'N/A'}</span>
              </div>
              <div className="info-row">
                <label>Email:</label>
                <span>{user?.email || 'N/A'}</span>
              </div>
              <div className="info-row">
                <label>Role:</label>
                <span className="role-badge">{user?.role || 'User'}</span>
              </div>
            </div>
          </div>

          {/* Change Password Section */}
          <div className="account-section">
            <h2 className="section-title">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-field">
                <label>New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  disabled={loading}
                />
              </div>

              <PasswordRequirements password={passwordData.newPassword} />

              <div className="form-field">
                <label>Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  disabled={loading}
                />
              </div>

              <button 
                type="submit" 
                className="submit-button" 
                disabled={loading || !validatePassword(passwordData.newPassword) || passwordData.newPassword !== passwordData.confirmPassword}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountInfo;
